// src/pages/teaching/Teaching_Master_Search.jsx
import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Button,
  Input,
  Table,
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { apiService } from '../../constants/ApiService';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import useVendorBranch from '../../utils/useVendorBranch';
import conditionalDataLoad from '../../utils/conditionalDataLoad';

const Teaching_Master_Search = () => {
  const navigate = useNavigate();

  // 🔹 user from Redux
  const { user } = useSelector((state) => state.authState);
  const { isSuperAdmin, isAdmin, hasMultiBranch, hasSingleBranch } =
    useVendorBranch(user);

  const [errorMessage, setErrorMessage] = useState({});
  const [selectionData, setSelectionData] = useState({
    vendor_id: user?.vendor_id || 0,
    branch_id: user?.branch_id || 0,
  });

  const [search, setSearch] = useState('');
  const [data, setData] = useState([]);

  // ✅ FIXED unwrap: axios response vs direct object
  const unwrap = (res) => {
    if (!res) return res;
    // axios response has numeric status and "data"
    if (typeof res?.status === 'number' && res?.data !== undefined) {
      return res.data;
    }
    return res;
  };

  // ✅ FIXED: pick message + list from any response shape
  const getMsg = (raw) => raw?.Message || raw?.message || '';
  const getList = (raw) =>
    raw?.Data ||
    raw?.data ||
    raw?.Data1 ||
    raw?.data1 ||
    raw?.studentFeeInstallments || // (extra safe)
    [];

  const onAddClick = () => {
    navigate('/batch-card-list-teaching-log');
  };

  const onEditClick = (item) => {
    navigate('/save-teaching', { state: { ...item } });
  };

  const onDeleteClick = async (id) => {
    const ok = window.confirm('Are you sure you want to delete this record?');
    if (!ok) return;

    try {
      let config = {
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
      };

      const response = await apiService.delete(
        `api/TeachingMaster/DeleteTeachingMaster?id=${id}`,
        config
      );

      // enrollment-style error surface
      if (response?.response && response?.response?.data?.errors) {
        alert(response.response.data.errors);
      }

      const raw = unwrap(response);

      const success = raw?.status === true;
      alert(getMsg(raw) || (success ? 'Deleted' : 'Delete failed'));

      if (success) GetTeachingMaster(search);
    } catch (error) {
      console.log(error);
      alert('Something went wrong!!');
    }
  };

  // ✅ MAIN GET (LIST) — SEARCH works
  const GetTeachingMaster = async (searchVal = '') => {
    try {
      const vendorId = selectionData.vendor_id || '';
      const branchId = selectionData.branch_id || '';

      // ✅ keep your API names as-is
      const isFaculty =
        String(user?.user_profile || '').toLowerCase() === 'faculty';

      const apiName = isFaculty ? 'GetTeaching_ByFaculty' : 'GetTeachingMaster';

      const url = `api/TeachingMaster/${apiName}?vendorId=${vendorId}&branchId=${branchId}&search=${encodeURIComponent(
        searchVal || ''
      )}`;

      const response = await apiService.get(url);

      // enrollment-style error surface
      if (response?.response && response?.response?.data?.errors) {
        alert(response.response.data.errors);
      }

      const raw = unwrap(response);

      if (raw?.status === true) {
        const list = getList(raw);
        setData(Array.isArray(list) ? list : []);
      } else {
        setData([]);
        alert(getMsg(raw) || 'No data');
      }
    } catch (error) {
      console.log(error);
      alert('Something went wrong!!');
      setData([]);
    }
  };

  const onSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    GetTeachingMaster(val);
  };

  // 🔹 vendor/branch change pe reload – same conditionalDataLoad pattern
  useEffect(() => {
    const canLoad = conditionalDataLoad({
      hasMultiBranch,
      hasSingleBranch,
      isSuperAdmin,
      isAdmin,
      vendorId: selectionData.vendor_id,
      branchId: selectionData.branch_id,
    });

    if (canLoad) {
      GetTeachingMaster(search);
    } else {
      setData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionData.vendor_id, selectionData.branch_id]);

  return (
    <>
      <div className='text-right mb-2'>
        <Button color='primary' size='sm' onClick={onAddClick}>
          <i className='bi bi-plus-circle me-1'></i> Add Teaching Record
        </Button>
      </div>

      <VendorBranchSelector
        user={user}
        selectionData={selectionData}
        setSelectionData={setSelectionData}
        errorMessage={errorMessage}
        accessButton={true}
      />

      <Col lg='12'>
        <Card className='card-box height-100-p pd-20 mb-20'>
          <CardTitle
            tag='h6'
            className='p-3 mb-0 d-flex align-items-center justify-content-between'
          >
            <div className='d-flex align-items-center'>
              <i className='bi bi-card-text mr-2'></i>
              Teaching Master Details
            </div>
          </CardTitle>

          <div
            className='col-12 d-flex justify-content-center'
            style={{ paddingRight: '10px', paddingLeft: '10px' }}
          >
            <Input
              id='search'
              name='search'
              placeholder='Search...'
              type='text'
              onChange={onSearchChange}
              className='mx-auto'
              value={search}
            />
          </div>

          <CardBody>
            <div className='table-responsive'>
              <Table striped hover responsive>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Edit</th>
                    <th>Date</th>
                    <th>Topic Covered</th>
                    <th>Homework</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data && data.length > 0 ? (
                    data.map((item, index) => (
                      <tr key={item.id ?? index}>
                        <td>{index + 1}</td>

                        <td width={2}>
                          <i
                            style={{ cursor: 'pointer' }}
                            onClick={() => onEditClick(item)}
                            className='bi bi-pencil text-primary'
                          ></i>
                        </td>

                        <td>
                          {item.date ? String(item.date).substring(0, 10) : ''}
                        </td>

                        <td>{item.topic_covered}</td>
                        <td>{item.homework}</td>

                        <td>
                          <i
                            className='bi bi-trash text-danger'
                            style={{ cursor: 'pointer', fontSize: '1.1rem' }}
                            title='Delete'
                            onClick={() => onDeleteClick(item.id)}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} style={{ textAlign: 'center' }}>
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      </Col>
    </>
  );
};

export default Teaching_Master_Search;
